import { useTranslation } from 'react-i18next';
import type { IAdminTag, TagStatus } from '@interfaces/IAdminTag';
import { Tooltip } from '@components/ui/Tooltip/Tooltip';
import { TradePill } from '@components/ui/TradePill/TradePill';
import { tradeLabel } from '@utils/tradeLabel';
import { professionDisplayText } from '@utils/professionDisplay';
import { getBatteryLevelNumber, getDaysAgo, hasExpiredCertificate, resolveTagProfessionColor, ADMIN_PANEL_LAST_SEEN_DAYS } from '../utils/tagFilters';
import { useProfessionColorMap } from '../ProfessionColorContext';
import { BatteryGlyph, LastSeenIcon, TagStatusIcon } from './tagIcons';
import table from '../../adminTable.module.css';
import styles from '../styles.module.css';
import { useSelector } from 'react-redux';
import { selectSiteTz } from '@store/selectors/site.selectors';

const ICON_STATUSES: ReadonlySet<TagStatus> = new Set<TagStatus>(['Good', 'Sleeping', 'Storage', 'NoReception']);

// Tag last-seen / certificate dates belong to the SITE, so they render in the
// project's zone. (This block used to assert "site timezone is always JST —
// product is Japan-only"; sites outside JST exist, so the zone is a parameter.)
const fmtDateTime = (tz: string) =>
  new Intl.DateTimeFormat('ja-JP', {
    timeZone: tz,
    month: 'numeric',
    day: 'numeric',
    hour: '2-digit',
    minute: '2-digit',
  });
const fmtDate = (tz: string) => new Intl.DateTimeFormat('ja-JP', { timeZone: tz });

/** Returns null when `iso` is missing or unparseable — callers render "—". */
function fmtStamp(iso: string | null | undefined, fmt: Intl.DateTimeFormat): string | null {
  if (!iso) return null;
  const d = new Date(iso);
  return Number.isNaN(d.getTime()) ? null : fmt.format(d);
}

export function StatusCell({ status }: { status: TagStatus }) {
  const { t } = useTranslation();
  const label = t(`adminTags.status.${status}`);
  if (!ICON_STATUSES.has(status)) {
    return (
      <Tooltip content={label}>
        <span className={table.muted}>—</span>
      </Tooltip>
    );
  }
  return (
    <Tooltip content={label}>
      <span className={styles.glyph} aria-label={label} role="img">
        <TagStatusIcon status={status} />
      </span>
    </Tooltip>
  );
}

function batteryTone(level: number): 'high' | 'medium' | 'low' {
  if (level > 60) return 'high';
  if (level > 30) return 'medium';
  return 'low';
}

export function BatteryCell({ battery }: { battery: string | null }) {
  if (battery == null) return <span className={table.muted}>—</span>;
  const level = getBatteryLevelNumber(battery);
  return (
    <Tooltip content={battery}>
      <span className={styles.glyph} aria-label={battery} role="img">
        <BatteryGlyph tone={batteryTone(level)} />
      </span>
    </Tooltip>
  );
}

function lastSeenTone(days: number): 'ok' | 'warning' | 'alert' {
  if (days <= ADMIN_PANEL_LAST_SEEN_DAYS.OK_MAX_DAYS) return 'ok';
  if (days <= ADMIN_PANEL_LAST_SEEN_DAYS.WARNING_MAX_DAYS) return 'warning';
  return 'alert';
}

export function LastSeenCell({ lastSeen, floorNick }: { lastSeen: string | null; floorNick?: string | null }) {
  // The tag belongs to a site; its last-seen stamp reads in that site's zone.
  const tz = useSelector(selectSiteTz);
  if (!lastSeen || !tz) return <span className={table.muted}>—</span>;
  const days = getDaysAgo(lastSeen);
  const date = fmtStamp(lastSeen, fmtDateTime(tz));
  if (!date) return <span className={table.muted}>—</span>;
  const title = floorNick ? `${floorNick} · ${date}` : date;
  return (
    <Tooltip content={title}>
      <span className={styles.glyph} aria-label={title} role="img">
        <LastSeenIcon tone={lastSeenTone(days)} />
      </span>
    </Tooltip>
  );
}

export function CertificatesCell({ tag }: { tag: IAdminTag }) {
  const { t } = useTranslation();
  const tz = useSelector(selectSiteTz);
  if (tag.certificates.length === 0 || !tz) return <span className={table.muted}>—</span>;
  const expired = hasExpiredCertificate(tag);
  const title = tag.certificates
    .map((c) => {
      const d = fmtStamp(c.expirationDate, fmtDate(tz));
      return d ? `${c.name} (${d})` : c.name;
    })
    .join('\n');
  return (
    <span
      className={`${styles.certBadge} ${expired ? styles.toneAlert : styles.toneGood}`}
      title={title}
    >
      {expired ? t('adminTags.cert.expired') : t('adminTags.cert.valid')}
      <span className={styles.certCount}>{tag.certificates.length}</span>
    </span>
  );
}

export function LinkedToCell({ tag }: { tag: IAdminTag }) {
  const { t } = useTranslation();
  const colorByProfessionId = useProfessionColorMap();
  // Mirrors trusstor's TagEntityChip. Workers: translated trade, then
  // ", specialty" and " - contractor" appended. Managers/utilities: their
  // translated trade (colour + pictogram), falling back to role/name when the
  // upstream omits a trade.
  let label: string;
  let color: string | undefined;
  let tradeId: string | undefined;
  if (tag.profession) {
    label = tag.profession.tradeId
      ? professionDisplayText(t, tag.profession)
      : (tag.profession.tradeGroup ?? '');
    // Catalog wins over the colour embedded in the tag frame — same precedence
    // Scheduler's ProfessionPill applies, so a profession recoloured for this
    // project reads identically here, in Monitor and in the sidebar.
    color = resolveTagProfessionColor(tag.profession, colorByProfessionId);
    tradeId = tag.profession.tradeId;
  } else if (tag.manager) {
    label = tag.manager.tradeId ? tradeLabel(t, tag.manager.tradeId) : tag.manager.role || tag.manager.name || '';
    color = tag.manager.backgroundColor ?? undefined;
    tradeId = tag.manager.tradeId ?? undefined;
  } else if (tag.linkedUtility) {
    label = tag.linkedUtility.tradeId ? tradeLabel(t, tag.linkedUtility.tradeId) : tag.linkedUtility.name;
    color = tag.linkedUtility.backgroundColor ?? undefined;
    tradeId = tag.linkedUtility.tradeId ?? undefined;
  } else {
    label = '';
  }
  if (!label) return <span className={table.muted}>{t('adminTags.unassigned')}</span>;
  return (
    <Tooltip content={label}>
      <span className={styles.linkedWrap}>
        <TradePill
          label={label}
          {...(color ? { backgroundColor: color } : {})}
          {...(tradeId ? { tradeId } : {})}
        />
      </span>
    </Tooltip>
  );
}
